import { useCallback, useEffect, useState } from "react";
import {
  deleteAdminBranch,
  fetchAdminBranches,
  updateAdminBranchStatus,
  type AdminBranch,
} from "../api/adminApi";
import { getAuthToken } from "../auth";
import { branches as mockBranches } from "../data";
import type { Branch } from "../types";

function mapApiBranch(row: AdminBranch): Branch {
  return {
    id: String(row.id),
    name: row.name,
    province: row.province || "—",
    phone: row.phone || "",
    email: row.email || "",
    status: row.status,
  };
}

export function useAdminBranches() {
  const [branches, setBranches] = useState<Branch[]>(mockBranches);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setBranches(mockBranches);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = await fetchAdminBranches();
      setBranches(api.branches.map(mapApiBranch));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les antennes.");
      setBranches(mockBranches);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const deactivate = useCallback(
    async (branch: Branch) => {
      if (!usingApi) {
        setBranches((current) =>
          current.map((row) => (row.id === branch.id ? { ...row, status: "inactive" } : row)),
        );
        return;
      }
      const updated = await updateAdminBranchStatus(Number(branch.id), "inactive");
      setBranches((current) =>
        current.map((row) => (row.id === branch.id ? mapApiBranch(updated) : row)),
      );
    },
    [usingApi],
  );

  const remove = useCallback(
    async (branch: Branch) => {
      if (!usingApi) {
        setBranches((current) => current.filter((row) => row.id !== branch.id));
        return;
      }
      await deleteAdminBranch(Number(branch.id));
      setBranches((current) => current.filter((row) => row.id !== branch.id));
    },
    [usingApi],
  );

  return {
    branches,
    loading,
    error,
    usingApi,
    reload: load,
    deactivate,
    remove,
  };
}
